// ============================================
// PRIMITIVES UI — socle du design system
// ============================================
// Composants purement présentationnels : aucun hook, aucun accès au contexte
// ni au thème. Les couleurs viennent des jetons KLY (tailwind.config.js) :
// sapin pour l'action principale, trait pour les contours, encre/graphite
// pour le texte. Une surface n'a pas d'ombre ; seule une fenêtre en a une.

import React from 'react';
import { createPortal } from 'react-dom';
import logoKly from '../../assets/logo-kly.png';
import { X } from 'lucide-react';

// --- Téléphone et tablette -------------------------------------------------
// Toutes ces classes passent par max-sm: / max-lg: : elles ne s'appliquent
// qu'en dessous de 640 / 1024 px. Le rendu sur ordinateur (lg et plus) reste
// celui d'avant.

/** Cible tactile d'au moins 40 × 40 px sous lg (téléphone, tablette). */
export const CIBLE_TACTILE = 'max-lg:min-h-10 max-lg:min-w-10';
/** Zone tactile élargie de 8 px autour d'un petit contrôle (interrupteur) sans changer son dessin. L'élément doit être `relative`. */
export const ZONE_TACTILE = "max-lg:after:absolute max-lg:after:-inset-2 max-lg:after:content-['']";
/** Fenêtre écrite à la main : son fond (conteneur fixe centré) ne laisse plus de marge sur téléphone… */
export const FENETRE_FOND_MOBILE = 'max-sm:p-0 max-sm:items-stretch';
/** … et son panneau occupe tout l'écran, avec défilement interne. */
export const FENETRE_MOBILE = 'max-sm:max-w-none max-sm:h-full max-sm:max-h-none max-sm:rounded-none max-sm:border-0 max-sm:overflow-y-auto';

/**
 * Rend une fenêtre directement dans <body>, hors de l'arbre de la page.
 * Chaque écran est monté dans `.anim-page`, dont l'animation laisse un
 * `transform` appliqué : un élément `fixed` placé dedans se calerait sur le
 * contenu de la page (et défilerait avec lui) au lieu de l'écran. Le portail
 * garde le voile et la fenêtre calés sur l'écran. Les événements React
 * continuent de remonter vers le composant parent.
 */
export const Fenetre: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  typeof document === 'undefined' ? <>{children}</> : createPortal(children, document.body);

// Logo officiel KLY Groupe (globe + « KLY GROUPE »), même fichier que les
// e-mails Brevo. `inverse` sur fond sombre : pastille blanche ; `compact` :
// le globe seul (barre latérale repliée).
export const Wordmark: React.FC<{ inverse?: boolean; compact?: boolean; sousTitre?: string }> = ({ inverse = false, compact = false, sousTitre = 'Service après-vente' }) => (
  <div className="flex items-center gap-3 select-none" aria-label={`KLY Groupe, ${sousTitre.toLowerCase()}`}>
    <span className={inverse ? 'rounded-xl bg-white px-2 py-1 shadow-sm' : ''}>
      {compact
        ? <img src={logoKly} alt="KLY Groupe" className="w-9 h-9 object-cover object-right" />
        : <img src={logoKly} alt="KLY Groupe" className="h-9 w-auto" />}
    </span>
    {!compact && (
      <span className={`text-[12px] font-semibold leading-tight border-l pl-3 ${inverse ? 'text-white/90 border-white/30' : 'text-sapin-700 border-trait'}`}>{sousTitre}</span>
    )}
  </div>
);

export const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>{children}</span>
);

export const Button: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit';
  title?: string;
  /** Nom accessible, pour un bouton réduit à son icône. */
  'aria-label'?: string;
}> = ({ children, onClick, variant = 'primary', size = 'md', disabled = false, className = '', type = 'button', title, 'aria-label': ariaLabel }) => {
  const v = {
    primary: 'bg-sapin-700 text-white hover:bg-sapin-800',
    secondary: 'bg-white text-encre border border-trait hover:bg-brume',
    danger: 'bg-statut-urgent text-white hover:bg-red-800',
    ghost: 'bg-transparent text-graphite hover:bg-brume hover:text-encre',
    success: 'bg-sapin-700 text-white hover:bg-sapin-800'
  };
  const s = { xs: 'px-2 py-1 text-xs', sm: 'px-2.5 py-1.5 text-xs', md: 'px-3.5 py-2 text-sm', lg: 'px-5 py-3 text-base' };
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} aria-label={ariaLabel}
      className={`inline-flex items-center justify-center font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${CIBLE_TACTILE} ${v[variant]} ${s[size]} ${className}`}>
      {children}
    </button>
  );
};

export const Card: React.FC<{ children: React.ReactNode; className?: string; onClick?: () => void }> = ({ children, className = '', onClick }) => (
  <div onClick={onClick} className={`bg-white rounded-card border border-trait ${onClick ? 'cursor-pointer hover:border-graphite/50 transition-colors duration-150' : ''} ${className}`}>
    {children}
  </div>
);

export const Input: React.FC<{
  type?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoFocus?: boolean;
  'aria-label'?: string;
  /** Ex. « off » pour une recherche, « new-password » pour un secret : sans
   *  lui, Chrome prend un écran à champ mot de passe pour un formulaire de
   *  connexion et remplit le champ texte précédent avec l'e-mail enregistré. */
  autoComplete?: string;
  name?: string;
}> = ({ type = 'text', placeholder, value, defaultValue, onChange, className = '', icon, disabled, readOnly, onKeyDown, autoFocus, 'aria-label': ariaLabel, autoComplete, name }) => (
  <div className="relative">
    {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-graphite">{icon}</div>}
    {/* 16 px sur téléphone : en dessous, iOS agrandit la page à la saisie. */}
    <input type={type} placeholder={placeholder} value={value} defaultValue={defaultValue} onChange={onChange} disabled={disabled} readOnly={readOnly} onKeyDown={onKeyDown} autoFocus={autoFocus} aria-label={ariaLabel} autoComplete={autoComplete} name={name}
      className={`block w-full rounded-lg border border-trait bg-white py-2.5 ${icon ? 'pl-10' : 'pl-3.5'} pr-3.5 text-sm max-sm:text-base text-encre placeholder:text-graphite/70 focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none disabled:bg-brume transition-colors duration-150 ${className}`} />
  </div>
);

export const Textarea: React.FC<{
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  className?: string;
}> = ({ placeholder, value, onChange, onKeyDown, rows = 3, className = '' }) => (
  <textarea placeholder={placeholder} value={value} onChange={onChange} onKeyDown={onKeyDown} rows={rows}
    className={`block w-full rounded-lg border border-trait bg-white py-2.5 px-3.5 text-sm max-sm:text-base text-encre placeholder:text-graphite/70 focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none resize-none transition-colors duration-150 ${className}`} />
);

export const Select: React.FC<{
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  /** Nom accessible, quand aucun libellé n'est relié au champ. */
  'aria-label'?: string;
}> = ({ value, onChange, options, placeholder, className = '', disabled, id, 'aria-label': ariaLabel }) => (
  <select value={value} onChange={onChange} disabled={disabled} id={id} aria-label={ariaLabel}
    className={`block w-full rounded-lg border border-trait bg-white py-2.5 pl-3.5 pr-10 text-sm max-sm:text-base text-encre focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none disabled:bg-brume disabled:cursor-wait transition-colors duration-150 ${className}`}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}> = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl', full: 'max-w-6xl' };
  // Sur téléphone (< 640 px), la fenêtre occupe tout l'écran : plus de marge,
  // plus de coins arrondis, hauteur de l'écran visible (100dvh quand le
  // navigateur la connaît, sinon 100vh).
  return (
    <Fenetre>
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 max-sm:p-0 max-sm:min-h-full max-sm:items-stretch">
        <div className="fixed inset-0 bg-encre/50 transition-opacity" onClick={onClose} />
        <div role="dialog" aria-modal="true" aria-label={title} className={`relative bg-white rounded-card border border-trait shadow-flottant ${sizes[size]} w-full max-h-[90vh] flex flex-col animate-fade-in max-sm:max-w-none max-sm:h-screen max-sm:supports-[height:100dvh]:h-dvh max-sm:max-h-none max-sm:rounded-none max-sm:border-0`}>
          <div className="flex items-center justify-between gap-3 px-5 py-4 max-sm:px-4 max-sm:py-3 border-b border-trait flex-shrink-0">
            <h3 className="text-base font-bold text-encre min-w-0">{title}</h3>
            <button onClick={onClose} aria-label="Fermer" className={`p-2 text-graphite hover:text-encre hover:bg-brume rounded-lg transition-colors flex-shrink-0 inline-flex items-center justify-center ${CIBLE_TACTILE}`}><X className="w-5 h-5" /></button>
          </div>
          <div className="p-5 max-sm:p-4 overflow-y-auto flex-1">{children}</div>
        </div>
      </div>
    </div>
    </Fenetre>
  );
};

export const Tabs: React.FC<{
  tabs: { id: string; label: string; icon?: React.ReactNode; count?: number }[];
  activeTab: string;
  onChange: (id: string) => void;
}> = ({ tabs, activeTab, onChange }) => (
  // Sur téléphone, les onglets défilent à l'horizontale ; le bord droit
  // s'estompe pour signaler qu'il en reste à voir.
  <div className="border-b border-trait overflow-x-auto overscroll-x-contain max-sm:[-webkit-mask-image:linear-gradient(to_right,#000_calc(100%_-_24px),transparent)] max-sm:[mask-image:linear-gradient(to_right,#000_calc(100%_-_24px),transparent)]">
    <nav className="flex gap-1 min-w-max max-sm:pr-6" role="tablist">
      {tabs.map(t => (
        <button key={t.id} role="tab" aria-selected={activeTab === t.id} onClick={() => onChange(t.id)}
          className={`flex items-center gap-2 py-3 px-3.5 max-sm:px-3 -mb-px border-b-2 text-sm font-semibold whitespace-nowrap transition-colors duration-150 ${activeTab === t.id ? 'border-sapin-700 text-sapin-900' : 'border-transparent text-graphite hover:text-encre'}`}>
          {t.icon}{t.label}{t.count !== undefined && <span className={`tnum ml-0.5 px-1.5 py-0.5 rounded-full text-xs ${activeTab === t.id ? 'bg-sapin-100 text-sapin-900' : 'bg-brume text-graphite'}`}>{t.count}</span>}
        </button>
      ))}
    </nav>
  </div>
);
